<script setup>
import calificaciones from '@/api/calificaciones';
import ModalSimple from '@/components/ModalSimple.vue';
import TipoNota from '@/models/calificaciones/TipoNota';
import { modelErrorsToHtmlText } from '@/libs/cifra/model_errors';
import {
  computed,
  ref,
  watch,
} from 'vue';
import NotificacionForm from './NotificacionForm.vue';
import { useTipoNotaActual } from './notificaciones';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  itemDiarioDefinicion: { type: Object, required: true },
  error: { type: [String, Array], default: null },
});

const emits = defineEmits(['update:modelValue']);

const notificaciones = ref([...props.modelValue]);
const notificacionActual = ref(null);
const showModal = ref(false);
const tiposNotas = ref([]);

const { tipoNotaActual } = useTipoNotaActual(tiposNotas);

const notificacionesJson = computed(() => JSON.stringify(notificaciones.value));

watch(() => props.modelValue, (nuevasOpciones) => {
  notificaciones.value = [...nuevasOpciones];
});

watch(notificaciones, (valor) => {
  emits('update:modelValue', valor);
}, { deep: true });

function eliminar(notificacion) {
  const index = notificaciones.value.indexOf(notificacion);
  if (index > -1) {
    notificaciones.value.splice(index, 1);
  }
}

function anadir() {
  notificacionActual.value = {
    id: `new-${Date.now()}`,
    valor: '',
    mensaje: '',
  };
  showModal.value = true;
}

function editar(notificacion) {
  notificacionActual.value = { ...notificacion };
  showModal.value = true;
}

function guardarNotificacion(notificacion) {
  const index = notificaciones.value.findIndex((n) => n.id === notificacion.id);
  if (index > -1) {
    notificaciones.value[index] = { ...notificacion };
  } else {
    notificaciones.value.push({ ...notificacion });
  }
  showModal.value = false;
}

async function cargarTiposNotas() {
  const result = await calificaciones.maestros.getTiposNotas();
  tiposNotas.value = result.data.map((tn) => new TipoNota(tn));
}

// Cargamos los tipos de notas al crear el componente
cargarTiposNotas();
</script>

<template>
  <div>
    <ModalSimple
      v-model="showModal"
      :titulo="$t('Notificacion')"
      tamano="lg"
      @close="showModal = false"
    >
      <NotificacionForm
        v-if="notificacionActual && showModal"
        v-model="notificacionActual"
        :item-diario-definicion="itemDiarioDefinicion"
        :tipos-notas="tiposNotas"
        @guardar="guardarNotificacion"
        @cancelar="showModal = false"
      />
    </ModalSimple>
    <input
      v-model="notificacionesJson"
      name="calificaciones_item_diario_definicion[notificaciones_json]"
      type="hidden"
    />
    <Transition enter-active-class="animated fadeIn">
      <div v-if="notificaciones.length === 0" class="alert alert-info">
        <button
          type="button"
          class="btn btn-cifra btn-xs m-l-sm pull-right"
          @click="anadir"
        >
          <i class="fa fa-plus"></i>
          {{ $t('Anadir') }}
        </button>
        <i class="fa fa-info-circle"></i> {{ $t('no_notificaciones_definidas') }}
      </div>
      <table v-else class="table table-borderless cifra-table">
        <thead class="border-bottom">
          <tr>
            <th class="col-min">
              {{ $t('valor') }}
            </th>
            <th>
              {{ $t('nombre_nota') }}
            </th>
            <th class="col-min text-center">
              <button
                type="button"
                class="btn btn-cifra btn-xs"
                @click="anadir"
              >
                <i class="fa fa-plus"></i>
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="notificacion in notificaciones" :key="notificacion.id">
            <td>
              {{ notificacion.valor }}
            </td>
            <td class="text-truncate va-m" style="max-width: 1px;">
              {{ tipoNotaActual?.nombreParaValor(notificacion.valor) }}
            </td>
            <td class="text-nowrap text-center va-m">
              <i
                class="fa fa-edit clickable m-r-xs"
                @click="editar(notificacion)"
              ></i>
              <i
                v-confirmar="$t('seguro_eliminar_registro')"
                class="fa fa-trash text-danger clickable"
                @confirmado="eliminar(notificacion)"
              ></i>
            </td>
          </tr>
        </tbody>
      </table>
    </Transition>
    <p v-if="error" class="text-danger">
      {{ modelErrorsToHtmlText(error) }}
    </p>
  </div>
</template>
